import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    ArrowLeft, Save, Eye, EyeOff, ChevronDown,
    Loader2, FileText, Mail, Type, AlignLeft,
    MessageSquare, Sparkles, Send, Bot, User,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';

const PLACEHOLDERS = [
    { label: 'Employee First Name', value: '{employee.first_name}' },
    { label: 'Employee Full Name', value: '{employee.full_name}' },
    { label: 'Employee Email', value: '{employee.email}' },
    { label: 'Start Date', value: '{employee.start_date}' },
    { label: 'Team', value: '{employee.team}' },
    { label: 'Manager Name', value: '{employee.manager.name}' },
    { label: 'Company Name', value: '{company.name}' },
    { label: 'Company Website', value: '{company.website}' },
    { label: 'Sender Name', value: '{sender.name}' },
    { label: 'Sender Title', value: '{sender.title}' },
    { label: 'Sender Email', value: '{sender.email}' },
];

const EmailTemplateEditor = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const isNew = id === 'new';
    const defaultType = searchParams.get('type') || 'onboarding';

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        template_type: defaultType,
        subject: '',
        greeting: '',
        body: '',
        closing: '',
        signature: '',
        is_default: false,
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    // Preview
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Placeholder dropdown
    const [showPlaceholders, setShowPlaceholders] = useState(false);
    const [activeField, setActiveField] = useState('body');
    const fieldRefs = {
        name: useRef(null),
        subject: useRef(null),
        greeting: useRef(null),
        body: useRef(null),
        closing: useRef(null),
        signature: useRef(null),
    };

    // AI Chat
    const [showAI, setShowAI] = useState(false);
    const [aiMessages, setAiMessages] = useState([]);
    const [aiInput, setAiInput] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const aiChatEndRef = useRef(null);

    // Fetch template if editing
    useEffect(() => {
        if (!isNew) {
            const fetchTemplate = async () => {
                try {
                    const res = await api.get(`/email-templates/${id}`);
                    const t = res.data.template;
                    setFormData({
                        name: t.name || '',
                        template_type: t.template_type || 'onboarding',
                        subject: t.subject || '',
                        greeting: t.greeting || '',
                        body: t.body || '',
                        closing: t.closing || '',
                        signature: t.signature || '',
                        is_default: t.is_default || false,
                    });
                } catch (error) {
                    toast.error('Failed to load template');
                    navigate('/admin/settings');
                } finally {
                    setLoading(false);
                }
            };
            fetchTemplate();
        }
    }, [id, isNew, navigate]);

    // Auto-scroll AI chat
    useEffect(() => {
        aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [aiMessages]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const insertPlaceholder = (placeholder) => {
        const ref = fieldRefs[activeField]?.current;

        if (ref && typeof ref.insertText === 'function') {
            // It's a RichTextEditor component
            ref.insertText(placeholder);
        } else if (ref && (ref instanceof HTMLInputElement || ref instanceof HTMLTextAreaElement)) {
            // It's a normal text input/textarea
            const start = ref.selectionStart || 0;
            const end = ref.selectionEnd || 0;
            const current = formData[activeField] || '';
            const newValue = current.substring(0, start) + placeholder + current.substring(end);
            handleChange(activeField, newValue);

            // Restore cursor
            setTimeout(() => {
                ref.focus();
                const newPos = start + placeholder.length;
                ref.setSelectionRange(newPos, newPos);
            }, 0);
        } else {
            // Fallback: append to end
            const newValue = (formData[activeField] || '') + placeholder;
            handleChange(activeField, newValue);
        }
        setShowPlaceholders(false);
    };

    const PlaceholderDropdown = ({ field }) => (
        <div className="relative inline-block ml-3 font-normal">
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    setActiveField(field);
                    setShowPlaceholders(showPlaceholders === field ? false : field);
                }}
                className="flex items-center gap-1.5 px-2 py-1 bg-info/10 text-info rounded-md text-xs font-medium hover:bg-info/20 transition-colors"
                title={`Insert placeholder into ${field}`}
            >
                <Type size={12} />
                Insert Placeholder
                <ChevronDown size={12} className={`transition-transform ${showPlaceholders === field ? 'rotate-180' : ''}`} />
            </button>

            {showPlaceholders === field && (
                <div className="absolute z-20 mt-1 left-0 w-64 bg-card border border-border rounded-xl shadow-xl py-2 max-h-64 overflow-y-auto">
                    <div className="px-3 py-1.5 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                        Insert into: {field}
                    </div>
                    {PLACEHOLDERS.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setActiveField(field);
                                insertPlaceholder(p.value);
                            }}
                            className="w-full text-left px-3 py-1.5 hover:bg-secondary transition-colors flex items-center justify-between"
                        >
                            <span className="text-xs text-foreground">{p.label}</span>
                            <code className="text-[10px] text-muted-foreground bg-secondary rounded px-1 py-0.5">{p.value}</code>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Template name is required');
            return;
        }
        if (!formData.subject.trim()) {
            toast.error('Subject line is required');
            return;
        }
        if (!formData.body.trim()) {
            toast.error('Body content is required');
            return;
        }

        setSaving(true);
        try {
            if (isNew) {
                await api.post('/email-templates', formData);
                toast.success('Template created!');
            } else {
                await api.put(`/email-templates/${id}`, formData);
                toast.success('Template saved!');
            }
            navigate('/admin/settings?tab=templates');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = async () => {
        if (isNew && !formData.body.trim()) {
            // Local preview for new unsaved templates
            setPreviewData({
                subject: formData.subject || '(No subject)',
                greeting: formData.greeting || '',
                body: formData.body || '(No content)',
                closing: formData.closing || '',
                signature: formData.signature || '',
            });
            setShowPreview(true);
            return;
        }

        if (!isNew) {
            setPreviewLoading(true);
            try {
                const res = await api.post(`/email-templates/${id}/preview`);
                setPreviewData(res.data.preview);
            } catch (error) {
                toast.error('Failed to generate preview');
            } finally {
                setPreviewLoading(false);
            }
        }
        setShowPreview(true);
    };

    // AI chat handler
    const handleAISend = async () => {
        if (!aiInput.trim() || aiLoading) return;

        const userMessage = aiInput.trim();
        setAiInput('');
        setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setAiLoading(true);

        try {
            // Use the admin AI endpoint to generate email content
            const token = localStorage.getItem('token');
            const baseURL = api.defaults.baseURL;

            const response = await fetch(`${baseURL}/admin/ai/conversations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            });
            const convData = await response.json();
            const convId = convData.conversation?.id;

            if (!convId) throw new Error('Failed to create AI conversation');

            // Send the message with template context
            const contextMessage = `You are helping write an email template for an HR platform. 
The template type is "${formData.template_type}" (sent to ${formData.template_type === 'preboarding' ? 'employees before their start date' : formData.template_type === 'onboarding' ? 'employees during onboarding' : 'existing active employees/users being invited'}).

Current template state:
- Name: ${formData.name || '(not set)'}
- Subject: ${formData.subject || '(not set)'}
- Greeting: ${formData.greeting || '(not set)'}
- Body: ${formData.body || '(not set)'}
- Closing: ${formData.closing || '(not set)'}

Available placeholders: ${PLACEHOLDERS.map(p => p.value).join(', ')}

User request: ${userMessage}

IMPORTANT: Respond with the email content directly. If suggesting template content, format it clearly with labels like "Subject:", "Greeting:", "Body:", "Closing:", "Signature:" so the user can easily copy sections. Use placeholders where appropriate.`;

            const msgResponse = await fetch(`${baseURL}/admin/ai/conversations/${convId}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ message: contextMessage }),
            });
            const msgData = await msgResponse.json();

            const aiReply = msgData.response?.content || msgData.message?.content || 'Sorry, I couldn\'t generate a response. Please try again.';
            setAiMessages(prev => [...prev, { role: 'assistant', content: aiReply }]);

            // Clean up conversation
            await fetch(`${baseURL}/admin/ai/conversations/${convId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            console.error('AI error:', error);
            setAiMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.'
            }]);
        } finally {
            setAiLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    const typeLabel = formData.template_type.charAt(0).toUpperCase() + formData.template_type.slice(1);

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-border bg-card">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/admin/settings?tab=templates')}
                        className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-lg font-bold text-foreground">
                            {isNew ? `New ${typeLabel} Template` : 'Edit Template'}
                        </h1>
                        <p className="text-xs text-muted-foreground">
                            {formData.template_type === 'preboarding' && 'Sent to employees before their start date'}
                            {formData.template_type === 'onboarding' && 'Sent to employees during onboarding'}
                            {formData.template_type === 'active' && 'Sent to existing employees or admin/manager users'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowAI(!showAI)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${showAI ? 'bg-accent text-white' : 'bg-accent/10 text-accent hover:bg-accent/20'}`}
                    >
                        <Sparkles size={16} />
                        <span className="hidden sm:inline">AI Assist</span>
                    </button>
                    <button
                        onClick={handlePreview}
                        disabled={previewLoading}
                        className="flex items-center gap-2 px-3 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors text-sm font-medium"
                    >
                        {previewLoading ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
                        <span className="hidden sm:inline">Preview</span>
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors text-sm font-medium shadow-lg shadow-accent/30"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        {isNew ? 'Create' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Content area */}
            <div className="flex-1 flex overflow-hidden">
                {/* Template Form */}
                <div className={`flex-1 overflow-y-auto p-4 lg:p-6 ${showAI ? 'lg:w-[60%]' : 'w-full'}`}>
                    <div className="max-w-3xl mx-auto space-y-6">
                        {/* Template Name */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-foreground mb-1.5">
                                Template Name
                                <PlaceholderDropdown field="name" />
                            </label>
                            <input
                                ref={fieldRefs.name}
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                onFocus={() => setActiveField('name')}
                                placeholder="e.g. Welcome Email - Friendly"
                                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                            />
                        </div>

                        {/* Type selector (new only) */}
                        {isNew && (
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                    Template Type
                                </label>
                                <select
                                    value={formData.template_type}
                                    onChange={(e) => handleChange('template_type', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                                >
                                    <option value="preboarding">Preboarding</option>
                                    <option value="onboarding">Onboarding</option>
                                    <option value="active">Active / User Invitation</option>
                                </select>
                            </div>
                        )}

                        {/* Subject */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                <span className="flex items-center gap-2">
                                    <Mail size={14} />
                                    Subject Line
                                </span>
                                <PlaceholderDropdown field="subject" />
                            </label>
                            <input
                                ref={fieldRefs.subject}
                                type="text"
                                value={formData.subject}
                                onChange={(e) => handleChange('subject', e.target.value)}
                                onFocus={() => setActiveField('subject')}
                                placeholder="e.g. Welcome to {company.name}, {employee.first_name}!"
                                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                            />
                        </div>

                        {/* Greeting */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                <span className="flex items-center gap-2">
                                    <MessageSquare size={14} />
                                    Greeting
                                </span>
                                <PlaceholderDropdown field="greeting" />
                            </label>
                            <input
                                ref={fieldRefs.greeting}
                                type="text"
                                value={formData.greeting}
                                onChange={(e) => handleChange('greeting', e.target.value)}
                                onFocus={() => setActiveField('greeting')}
                                placeholder="e.g. Hi {employee.first_name},"
                                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                            />
                        </div>

                        {/* Body */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                <span className="flex items-center gap-2">
                                    <AlignLeft size={14} />
                                    Email Body
                                </span>
                                <PlaceholderDropdown field="body" />
                            </label>
                            <div onFocus={() => setActiveField('body')}>
                                <RichTextEditor
                                    ref={fieldRefs.body}
                                    value={formData.body}
                                    onChange={(value) => handleChange('body', value)}
                                />
                            </div>
                        </div>

                        {/* Closing */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                Closing
                                <PlaceholderDropdown field="closing" />
                            </label>
                            <div onFocus={() => setActiveField('closing')}>
                                <RichTextEditor
                                    ref={fieldRefs.closing}
                                    value={formData.closing}
                                    onChange={(value) => handleChange('closing', value)}
                                />
                            </div>
                        </div>

                        {/* Signature */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-1.5">
                                Signature
                                <PlaceholderDropdown field="signature" />
                            </label>
                            <div onFocus={() => setActiveField('signature')}>
                                <RichTextEditor
                                    ref={fieldRefs.signature}
                                    value={formData.signature}
                                    onChange={(value) => handleChange('signature', value)}
                                />
                            </div>
                        </div>

                        {/* Default toggle */}
                        <div className="flex items-center gap-3 p-4 bg-secondary rounded-xl border border-border">
                            <button
                                onClick={() => handleChange('is_default', !formData.is_default)}
                                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${formData.is_default ? 'bg-accent' : 'bg-muted'}`}
                            >
                                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${formData.is_default ? 'left-7' : 'left-1'}`} />
                            </button>
                            <div>
                                <p className="text-sm font-medium text-foreground">Set as default template</p>
                                <p className="text-xs text-muted-foreground">This template will be pre-selected when adding new {typeLabel.toLowerCase()} employees</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Panel (right side) */}
                {showAI && (
                    <div className="hidden lg:flex w-[40%] flex-col border-l border-border bg-card">
                        {/* AI Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                    <Sparkles size={14} className="text-white" />
                                </div>
                                <span className="text-sm font-semibold text-foreground">AI Email Assistant</span>
                            </div>
                            <button
                                onClick={() => setShowAI(false)}
                                className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                            >
                                <EyeOff size={16} />
                            </button>
                        </div>

                        {/* Chat messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {aiMessages.length === 0 && (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center mb-3">
                                        <Sparkles size={20} className="text-violet-400" />
                                    </div>
                                    <p className="text-sm font-medium text-foreground mb-1">AI Email Assistant</p>
                                    <p className="text-xs text-muted-foreground max-w-[240px] mx-auto">
                                        Ask me to draft your email, improve the subject line, adjust tone, or suggest placeholders.
                                    </p>
                                    <div className="mt-4 space-y-2">
                                        {[
                                            'Draft a friendly welcome email',
                                            'Write a professional onboarding email',
                                            'Improve my subject line',
                                        ].map((s, i) => (
                                            <button
                                                key={i}
                                                onClick={() => { setAiInput(s); }}
                                                className="block w-full text-left px-3 py-2 bg-secondary rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                                            >
                                                "{s}"
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {aiMessages.map((msg, i) => (
                                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                                    {msg.role === 'assistant' && (
                                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Bot size={12} className="text-white" />
                                        </div>
                                    )}
                                    <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${msg.role === 'user'
                                        ? 'bg-accent text-white rounded-br-md'
                                        : 'bg-secondary text-foreground rounded-bl-md'
                                        }`}>
                                        {msg.content}
                                    </div>
                                    {msg.role === 'user' && (
                                        <div className="w-6 h-6 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <User size={12} className="text-accent" />
                                        </div>
                                    )}
                                </div>
                            ))}

                            {aiLoading && (
                                <div className="flex gap-2">
                                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Bot size={12} className="text-white" />
                                    </div>
                                    <div className="px-3 py-2 bg-secondary rounded-xl rounded-bl-md">
                                        <div className="flex gap-1">
                                            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={aiChatEndRef} />
                        </div>

                        {/* AI Input */}
                        <div className="p-3 border-t border-border">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={aiInput}
                                    onChange={(e) => setAiInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAISend()}
                                    placeholder="Ask AI for help..."
                                    className="flex-1 px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                                    disabled={aiLoading}
                                />
                                <button
                                    onClick={handleAISend}
                                    disabled={aiLoading || !aiInput.trim()}
                                    className="px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                            <div className="flex items-center gap-2">
                                <Eye size={18} className="text-accent" />
                                <h3 className="text-lg font-semibold text-foreground">Email Preview</h3>
                            </div>
                            <button
                                onClick={() => setShowPreview(false)}
                                className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {previewData ? (
                                <div className="bg-white rounded-xl p-6 text-gray-800 shadow-inner">
                                    <div className="border-b border-gray-200 pb-3 mb-4">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Subject</p>
                                        <p className="text-lg font-medium">{previewData.subject}</p>
                                    </div>
                                    {previewData.greeting && (
                                        <p className="text-base mb-3" dangerouslySetInnerHTML={{ __html: previewData.greeting }} />
                                    )}
                                    <div className="text-base leading-relaxed whitespace-pre-wrap mb-4" dangerouslySetInnerHTML={{ __html: previewData.body }} />
                                    {previewData.closing && (
                                        <p className="text-base mb-3" dangerouslySetInnerHTML={{ __html: previewData.closing }} />
                                    )}
                                    {previewData.signature && (
                                        <div className="border-t border-gray-200 pt-3 mt-4 text-sm text-gray-600 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: previewData.signature }} />
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-3 border-t border-border flex justify-end">
                            <button
                                onClick={() => setShowPreview(false)}
                                className="px-4 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors text-sm font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailTemplateEditor;
